"use client";

import { useEffect, useRef, useState } from "react";

import { resolveCurrentHref } from "@/components/nav/NavPanelItems";
import { useToast } from "@/components/ui/use-toast";
import {
  MAX_NAV_BOOKMARKS,
  collectVisibleNavLinks,
  normalizeBookmarkHref,
  resolveNavBookmarks,
} from "@/lib/navigation/nav-bookmarks";
import {
  addNavBookmark,
  listNavBookmarks,
  removeNavBookmark,
} from "@/server-actions/nav-bookmarks";

export const BOOKMARKS_KEY = "bookmarks";
export const BOOKMARKS_TITLE = "Bookmarks";
export const BOOKMARKS_SUBTITLE = "Your pages in this studio";
export const BOOKMARKS_EMPTY =
  "No bookmarks yet. Star a link in any menu, or bookmark the page you're on.";

/**
 * The side nav's bookmarks (SAL-3283): the member's saved pages in this
 * studio, and the three ways to change them — a link's star in any menu,
 * "Bookmark this page", and a bookmark's remove button. Changes show at once
 * and settle to the list the server answers with; on a failure the list goes
 * back and a toast says why.
 *
 * `workspaceId` null switches it off (no studio, or a training-mode user,
 * whose nav is deliberately one menu); the list reloads whenever the studio
 * or the member (`userId`, which impersonation changes) does. `currentHref` is the address the
 * person is on, query and hash included.
 */
export function useNavBookmarks({ workspaceId, userId, menus, currentHref }) {
  const { toast } = useToast();
  const [saved, setSaved] = useState([]);
  // Bumped by every load and change; only the newest one's answer is kept.
  const latest = useRef(0);
  // Changes reach the server one at a time, so the newest answer is read
  // after every earlier change has landed and lists them all.
  const queue = useRef(Promise.resolve());
  // Whose list this is (null once unmounted), and the last list the server
  // confirmed for them: what a failed change goes back to.
  const owner = useRef(null);
  const confirmed = useRef([]);
  const enabled = Boolean(workspaceId && userId);

  useEffect(() => {
    setSaved([]);
    confirmed.current = [];
    const request = ++latest.current;
    if (!workspaceId || !userId) return;
    owner.current = `${userId}:${workspaceId}`;
    listNavBookmarks()
      .then((result) => {
        if (request !== latest.current) return;
        confirmed.current = result.bookmarks || [];
        setSaved(confirmed.current);
      })
      .catch((error) => console.warn("Bookmarks unavailable:", error?.message));
    return () => {
      owner.current = null;
    };
  }, [workspaceId, userId]);

  const visibleLinks = collectVisibleNavLinks(menus);
  const items = resolveNavBookmarks(saved, visibleLinks);
  // Saved pages this viewer's menus no longer show. They still count toward
  // the limit, so the Bookmarks view lists them to be removed.
  const unavailable = saved.filter(
    (bookmark) => !items.some((item) => item.href === bookmark.href),
  );
  const isSaved = (href) => saved.some((bookmark) => bookmark.href === href);
  const full = saved.length >= MAX_NAV_BOOKMARKS;

  // Show the change now, then take the server's list; without one (the call
  // failed, or refused the page) go back to the last list it confirmed. A
  // change made while this one was queued or in flight answers later with
  // the newer list, so this answer is dropped rather than shown over it. A
  // change still queued when the member or studio switches is never sent:
  // the session it would go out under is no longer theirs.
  const settle = async (optimistic, request) => {
    const call = ++latest.current;
    const sentFor = owner.current;
    const current = () => sentFor !== null && owner.current === sentFor;
    setSaved(optimistic);
    const sent = queue.current.then(() =>
      current() ? request().catch(() => null) : undefined,
    );
    queue.current = sent;
    const result = await sent;
    if (result === undefined || !current()) return;
    if (result?.bookmarks) confirmed.current = result.bookmarks;
    // A failure with no list may have overtaken the first load, so read the
    // list again rather than fall back to an empty one.
    else if (call === latest.current) {
      const fresh = await listNavBookmarks().catch(() => null);
      if (!current()) return;
      if (fresh?.bookmarks) confirmed.current = fresh.bookmarks;
    }
    if (call === latest.current) setSaved(confirmed.current);
    if (!result || result.error) {
      toast({
        variant: "destructive",
        title: "Bookmarks didn't change",
        description: result?.error || "Try again in a moment.",
      });
    }
  };

  const add = (link) => {
    const href = normalizeBookmarkHref(link.href);
    if (!href || full || isSaved(href)) return;
    const bookmark = { href, label: link.label, iconName: link.iconName };
    settle([...saved, bookmark], () => addNavBookmark(bookmark));
  };

  const remove = (href) =>
    settle(
      saved.filter((bookmark) => bookmark.href !== href),
      () => removeNavBookmark(href),
    );

  // The page the person is on, under the icon of the menu link it sits
  // beneath. `label` is its menu link's when it has one; otherwise the
  // control names it by the page's own heading (NavBookmarks), and
  // `fallbackLabel` is that nearest link's, for a page with no heading.
  const currentPage = (() => {
    const href = normalizeBookmarkHref(currentHref || "");
    if (!enabled || !href || isSaved(href)) return null;
    const exact = visibleLinks.get(href);
    const nearest = visibleLinks.get(
      resolveCurrentHref([...visibleLinks.values()], href.split(/[?#]/)[0]),
    );
    return {
      href,
      label: exact?.label || "",
      fallbackLabel: nearest?.label || href,
      iconName: (exact || nearest)?.iconName,
    };
  })();

  return {
    enabled,
    items,
    unavailable,
    full,
    isSaved,
    toggle: (link) => (isSaved(link.href) ? remove(link.href) : add(link)),
    remove,
    // Offered only while it can be acted on: the page is not saved yet.
    currentPage,
    addCurrentPage: (label) =>
      currentPage &&
      add({ href: currentPage.href, label, iconName: currentPage.iconName }),
  };
}
